import { useEffect, useMemo, useState } from "react";
import api, { fmtDT, formatApiError } from "@/lib/api";
import { PageHeader, Loading, EmptyState } from "@/components/Shared";
import { toast } from "sonner";
import { Boxes, Plus, X, Search } from "lucide-react";

export default function AdminProducts() {
  const [items, setItems] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");

  const load = () => api.get("/admin/products").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); api.get("/admin/suppliers").then((r) => setSuppliers(r.data)); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    try {
      await api.post("/admin/products", form);
      toast.success("Produit ajouté");
      setOpen(false); setForm({}); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (items === null) return <Loading />;
  const input = "mt-1 w-full h-10 px-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-shadow text-sm";

  const categories = [...new Set(items.map((p) => p.category).filter(Boolean))];
  const filtered = items.filter((p) =>
    (!cat || p.category === cat) &&
    (!q || p.name.toLowerCase().includes(q.toLowerCase()) || (p.code || "").includes(q))
  );

  return (
    <div>
      <PageHeader title="Produits" subtitle={`${items.length} références au catalogue.`}>
        <button onClick={() => setOpen(true)} data-testid="add-product" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors duration-200"><Plus className="h-4 w-4" /> Ajouter</button>
      </PageHeader>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input data-testid="product-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (nom, code)..." className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-shadow text-sm" />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} className="h-10 px-3 rounded-lg border border-slate-300 text-sm font-medium bg-white">
          <option value="">Toutes catégories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? <EmptyState title="Aucun produit" icon={Boxes} /> : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="max-h-[600px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left sticky top-0"><tr>
              <th className="px-5 py-3 font-semibold">Produit</th><th className="px-5 py-3 font-semibold">Catégorie</th><th className="px-5 py-3 font-semibold">Fournisseur</th><th className="px-5 py-3 font-semibold text-right">Prix achat</th><th className="px-5 py-3 font-semibold text-right">Prix public</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.slice(0, 200).map((p) => (
                <tr key={p.id} className="hover:bg-slate-50 transition-colors duration-200" data-testid={`product-${p.id}`}>
                  <td className="px-5 py-3 font-semibold text-slate-900">{p.name}<div className="text-xs text-slate-400 num">{p.code}</div></td>
                  <td className="px-5 py-3 text-slate-500">{p.category}</td>
                  <td className="px-5 py-3 text-slate-500">{p.supplier_name || "—"}</td>
                  <td className="px-5 py-3 text-right num text-slate-600">{p.purchase_price ? fmtDT(p.purchase_price) : "—"}</td>
                  <td className="px-5 py-3 text-right num font-semibold text-slate-900">{p.public_price ? fmtDT(p.public_price) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {filtered.length > 200 && <div className="px-5 py-3 text-xs text-slate-400 border-t">Affichage limité à 200 résultats — affinez votre recherche.</div>}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="product-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="font-bold text-lg text-slate-900">Nouveau produit</h3><button onClick={() => setOpen(false)}><X className="h-5 w-5 text-slate-400" /></button></div>
            <div className="space-y-3">
              <div><label className="text-sm font-semibold text-slate-700">Nom</label><input data-testid="product-name" onChange={set("name")} className={input} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-semibold text-slate-700">Laboratoire</label><input onChange={set("laboratory")} className={input} /></div>
                <div><label className="text-sm font-semibold text-slate-700">Catégorie</label><input onChange={set("category")} className={input} /></div>
              </div>
              <div><label className="text-sm font-semibold text-slate-700">Fournisseur</label>
                <select onChange={set("supplier_id")} className={input} data-testid="product-supplier"><option value="">—</option>{suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
              </div>
            </div>
            <button onClick={save} data-testid="save-product" className="mt-5 w-full h-11 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold transition-colors duration-200">Enregistrer</button>
          </div>
        </div>
      )}
    </div>
  );
}
